import { useState } from "react";
import { Reveal } from "@/components/site/Reveal";
import { CLINIC } from "@/lib/data";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Video, Building2, CalendarClock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { id: "video", label: "Video Consultation", icon: Video, desc: "Meet Dr. Jain securely from the comfort of home — ideal for follow-ups & reviews." },
  { id: "in-person", label: "In-Person Visit", icon: Building2, desc: "Visit our warm clinic in St. Inez for examinations, procedures & first consults." },
];

export default function Consultation() {
  const [type, setType] = useState("video");
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/appointments", { ...form, consultation_type: type });
      trackEvent("generate_lead", { event_category: "appointment", consultation_type: type, source: "home" });
      toast.success("Request received! Dr. Jain's team will reach out to confirm your slot.");
      setForm({ name: "", email: "", phone: "", preferred_date: "", notes: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <section id="consultations" className="relative py-28 lg:py-36 bg-olive-deep text-cream grain overflow-hidden" data-testid="consultations">
      <div className="absolute top-0 -right-40 w-[40rem] h-[40rem] rounded-full bg-blush-dusty/20 blur-[100px]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-beige mb-4">Ways to consult</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
            Choose how you'd like <span className="italic text-blush-pale">to be cared for.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* type toggle + calendly */}
          <div className="space-y-6">
            {TYPES.map((t, i) => {
              const Icon = t.icon;
              const active = type === t.id;
              return (
                <Reveal key={t.id} delay={i * 0.1}>
                  <button onClick={() => {
                      setType(t.id);
                      if (t.id === "in-person") toast.info("Our clinic is in St. Inez, Goa. Fill in your details below and we'll confirm your in-person visit.");
                    }} data-testid={`consult-type-${t.id}`}
                    className={`w-full text-left p-8 rounded-3xl border transition-colors duration-300 ${
                      active ? "bg-cream text-olive-deep border-cream" : "bg-white/5 border-white/15 hover:border-white/40"
                    }`}>
                    <div className="flex items-start gap-5">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${active ? "bg-blush-dusty text-cream" : "bg-white/10 text-beige"}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-serif text-2xl">{t.label}</h3>
                        <p className={`font-sans text-sm mt-2 leading-relaxed ${active ? "text-olive-sage" : "text-beige/80"}`}>{t.desc}</p>
                      </div>
                    </div>
                  </button>
                </Reveal>
              );
            })}

            <Reveal delay={0.2}>
              <a href={CLINIC.calendly} target="_blank" rel="noopener noreferrer" data-testid="calendly-link"
                 className="group flex items-center justify-between gap-4 p-6 rounded-3xl bg-blush-dusty text-cream hover:bg-blush-dusty/90 transition-colors duration-300">
                <div className="flex items-center gap-4">
                  <CalendarClock className="w-6 h-6" />
                  <div>
                    <p className="font-serif text-xl leading-none">Prefer to self-schedule?</p>
                    <p className="font-sans text-xs text-cream/80 mt-1">Pick a 15-min slot instantly on Calendly</p>
                  </div>
                </div>
                <ArrowUpRight className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </a>
            </Reveal>
          </div>

          {/* booking form */}
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="appointment-form"
                  className="bg-cream text-olive-deep rounded-3xl p-8 lg:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.25)]">
              <p className="font-serif text-2xl mb-1">Request an appointment</p>
              <p className="font-sans text-sm text-olive-sage mb-7">
                You selected <span className="text-blush-dusty font-medium">{type === "video" ? "Video Consultation" : "In-Person Visit"}</span>
              </p>
              <div className="space-y-4">
                <Field label="Full name" value={form.name} onChange={set("name")} testid="appt-name" required />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="appt-email" required />
                  <Field label="Phone" value={form.phone} onChange={set("phone")} testid="appt-phone" required />
                </div>
                <Field label="Preferred date" type="date" value={form.preferred_date} onChange={set("preferred_date")} testid="appt-date" />
                <div>
                  <label className="font-sans text-xs tracking-widest uppercase text-olive-sage">Notes (optional)</label>
                  <textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="appt-notes"
                    className="mt-1.5 w-full rounded-xl border border-beige bg-white/60 px-4 py-3 font-sans text-sm outline-none focus:ring-2 focus:ring-olive-sage transition" />
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="appointment-submit"
                className="mt-6 w-full py-4 rounded-full bg-olive-deep text-cream font-sans text-sm hover:bg-blush-dusty transition-colors duration-300 disabled:opacity-60">
                {loading ? "Sending…" : "Request appointment"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="font-sans text-xs tracking-widest uppercase text-olive-sage">{label}</label>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-beige bg-white/60 px-4 py-3 font-sans text-sm outline-none focus:ring-2 focus:ring-olive-sage transition" />
    </div>
  );
}
